import { useEffect, useState, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { LanguageProvider } from "./contexts/LanguageContext";
import { FeatureFlagProvider } from "./contexts/FeatureFlagContext";
import { BrandingProvider } from "./contexts/BrandingContext";
import Deko from "./components/Deko";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import PreLaunchWrapper from "./pages/PreLaunchWrapper";
import DashboardHome from "./pages/DashboardHome";
import Dashboard from "./pages/Dashboard";
import AnalysisResults from "./pages/AnalysisResults";
import CatalogHistory from "./pages/CatalogHistory";
import Settings from "./pages/Settings";  // User Preferences
import Billing from "./pages/Billing";
import Governance from "./pages/Governance";
import SharedReport from "./pages/SharedReport";
import AdminPortal from "./pages/AdminPortal";
import MobileAnalyzer from "./pages/MobileAnalyzer";
import RefundRadar from "./pages/RefundRadar";
import DutyRiskMonitoring from "./pages/DutyRiskMonitoring";
import OrgSettings from "./pages/OrgSettings";
import MyCorrections from "./pages/MyCorrections";
import JoinOrg from "./pages/JoinOrg";
import TeamShared from "./pages/TeamShared";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import IEEPARecovery from "./pages/IEEPARecovery";
import Resources from "./pages/Resources";
import SupplyChainIntelligence from "./pages/SupplyChainIntelligence";

// Components
import CookieConsent from "./components/CookieConsent";

// Hooks
import { useIsMobile, useStandalone } from "./hooks/useIsMobile";
import { apiGet, apiPost, ApiError } from "./lib/apiClient";

// Auth Context
export const AuthContext = ({ children }) => {
  return children;
};

// Auth Callback Handler
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const userData = await apiPost('/api/auth/session', { session_id: sessionId }, { auth: false });
          localStorage.setItem('decoded_user', JSON.stringify(userData));
          navigate('/dashboard', { state: { user: userData }, replace: true });
        } catch (error) {
          if (!(error instanceof ApiError)) {
            console.error('Auth callback error:', error);
          }
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#1E5EFF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600 font-public-sans">Completing sign in...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Skip if user data passed from AuthCallback
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      // Check localStorage first
      const storedUser = localStorage.getItem('decoded_user');

      if (storedUser) {
        try {
          const userData = await apiGet('/api/auth/me');
          setUser(userData);
          setIsAuthenticated(true);
        } catch (error) {
          if (error instanceof ApiError) {
            localStorage.removeItem('decoded_user');
            localStorage.removeItem('decoded_token');
          }
          setIsAuthenticated(false);
        }
      } else {
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, [location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-12 h-12 border-4 border-[#1E5EFF] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const token = localStorage.getItem('decoded_token');

  return (
    <FeatureFlagProvider token={token}>
      <BrandingProvider token={token}>
        {children({ user, setUser })}
      </BrandingProvider>
    </FeatureFlagProvider>
  );
};

// Router wrapper to handle session_id
const AppRouter = () => {
  const location = useLocation();
  const [preLaunchMode, setPreLaunchMode] = useState(null);

  useEffect(() => {
    const checkPreLaunch = async () => {
      try {
        const data = await apiGet('/api/pre-launch/status', { auth: false });
        setPreLaunchMode(!!data?.pre_launch_mode);
      } catch {
        setPreLaunchMode(false);
      }
    };
    checkPreLaunch();
  }, []);

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Still loading pre-launch status
  if (preLaunchMode === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-12 h-12 border-4 border-[#1E5EFF] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Pre-launch mode: gate most routes
  if (preLaunchMode) {
    const searchParams = new URLSearchParams(location.search);
    const isAdminBypass = searchParams.get('admin') === 'true';

    return (
      <Routes>
        <Route path="/" element={<PreLaunchWrapper />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/report/:shareToken" element={<SharedReport />} />
        {/* Admin bypass (route-gating only — does NOT alter auth): /<route>?admin=true */}
        <Route path="/login" element={isAdminBypass ? <Login /> : <PreLaunchWrapper />} />
        <Route path="/signup" element={isAdminBypass ? <Signup /> : <PreLaunchWrapper />} />
        <Route path="/forgot-password" element={isAdminBypass ? <ForgotPassword /> : <PreLaunchWrapper />} />
        <Route path="/reset-password" element={isAdminBypass ? <ResetPassword /> : <PreLaunchWrapper />} />
        <Route path="/join-org" element={isAdminBypass ? <JoinOrg /> : <PreLaunchWrapper />} />
        <Route path="/join-team" element={isAdminBypass ? <JoinOrg /> : <PreLaunchWrapper />} />
        <Route
          path="/corrections"
          element={isAdminBypass
            ? <ProtectedRoute>{({ user, setUser }) => <MyCorrections user={user} setUser={setUser} />}</ProtectedRoute>
            : <PreLaunchWrapper />}
        />
        <Route
          path="/team-shared"
          element={isAdminBypass
            ? <ProtectedRoute>{({ user, setUser }) => <TeamShared user={user} setUser={setUser} />}</ProtectedRoute>
            : <PreLaunchWrapper />}
        />
        {/* Protected routes still work for already-logged-in admins */}
        <Route 
          path="/dashboard" 
          element={<ProtectedRoute>{({ user, setUser }) => <DashboardHome user={user} setUser={setUser} />}</ProtectedRoute>} 
        />
        <Route 
          path="/admin" 
          element={<ProtectedRoute>{({ user, setUser }) => <AdminPortal user={user} setUser={setUser} />}</ProtectedRoute>} 
        />
        <Route 
          path="/analyzer" 
          element={<ProtectedRoute>{({ user, setUser }) => <Dashboard user={user} setUser={setUser} />}</ProtectedRoute>} 
        />
        <Route 
          path="/analysis/:analysisId" 
          element={<ProtectedRoute>{({ user }) => <AnalysisResults user={user} />}</ProtectedRoute>} 
        />
        <Route 
          path="/history" 
          element={<ProtectedRoute>{({ user }) => <CatalogHistory user={user} />}</ProtectedRoute>} 
        />
        <Route 
          path="/billing" 
          element={<ProtectedRoute>{({ user, setUser }) => <Billing user={user} setUser={setUser} />}</ProtectedRoute>} 
        />
        <Route 
          path="/governance" 
          element={<ProtectedRoute>{({ user, setUser }) => <Governance user={user} setUser={setUser} />}</ProtectedRoute>} 
        />
        <Route 
          path="/organization" 
          element={<ProtectedRoute>{({ user, setUser }) => <OrgSettings user={user} setUser={setUser} />}</ProtectedRoute>} 
        />
        <Route 
          path="/organization/shared" 
          element={<ProtectedRoute>{({ user, setUser }) => <OrgSettings user={user} setUser={setUser} />}</ProtectedRoute>} 
        />
        <Route 
          path="/ieepa-recovery" 
          element={<ProtectedRoute>{({ user, setUser }) => <IEEPARecovery user={user} setUser={setUser} />}</ProtectedRoute>} 
        />
        <Route 
          path="/preferences" 
          element={<ProtectedRoute>{({ user, setUser }) => <Settings user={user} setUser={setUser} />}</ProtectedRoute>} 
        />
        <Route 
          path="/refund-radar" 
          element={<ProtectedRoute>{({ user, setUser }) => <RefundRadar user={user} setUser={setUser} />}</ProtectedRoute>} 
        />
        <Route 
          path="/supply-chain-intelligence" 
          element={<ProtectedRoute>{({ user, setUser }) => <SupplyChainIntelligence user={user} setUser={setUser} />}</ProtectedRoute>} 
        />
        <Route 
          path="/duty-risk-monitoring" 
          element={<ProtectedRoute>{({ user, setUser }) => <DutyRiskMonitoring user={user} setUser={setUser} />}</ProtectedRoute>} 
        />
        {/* Everything else → pre-launch */}
        <Route path="*" element={<PreLaunchWrapper />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <DashboardHome user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/analyzer" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <Dashboard user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/analysis/:analysisId" 
        element={
          <ProtectedRoute>
            {({ user }) => <AnalysisResults user={user} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/history" 
        element={
          <ProtectedRoute>
            {({ user }) => <CatalogHistory user={user} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/preferences" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <Settings user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      <Route path="/settings" element={<Navigate to="/preferences" replace />} />
      <Route 
        path="/corrections" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <MyCorrections user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/billing" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <Billing user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/governance" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <Governance user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/duty-risk-monitoring" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <DutyRiskMonitoring user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <AdminPortal user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      {/* Mobile PWA Routes */}
      <Route 
        path="/mobile" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <MobileAnalyzer user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/mobile/analyze" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <MobileAnalyzer user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/mobile/scan" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <MobileAnalyzer user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/refund-radar" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <RefundRadar user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/organization" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <OrgSettings user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      {/* Backward compat redirect */}
      <Route path="/team" element={<Navigate to="/organization" replace />} />
      <Route 
        path="/organization/shared" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <TeamShared user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      <Route path="/team/shared" element={<Navigate to="/organization/shared" replace />} />
      <Route path="/join-team" element={<JoinOrg />} />
      <Route path="/join-org" element={<JoinOrg />} />
      <Route path="/report/:shareToken" element={<SharedReport />} />
      <Route 
        path="/ieepa-recovery" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <IEEPARecovery user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      <Route path="/resources" element={<Resources />} />
      <Route 
        path="/supply-chain-intelligence" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <SupplyChainIntelligence user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      {/* Legal & Auth Pages */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Dark Mode Toggle
const DarkModeToggle = () => {
  const [dark, setDark] = useState(() => {
    return localStorage.getItem('decoded-dark-mode') === 'true';
  });

  useEffect(() => {
    if (dark) {
      document.documentElement.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark-mode');
    }
    localStorage.setItem('decoded-dark-mode', dark);
  }, [dark]);

  return (
    <button
      className="dark-mode-toggle no-invert"
      onClick={() => setDark(d => !d)}
      aria-label="Toggle dark mode"
      data-testid="dark-mode-toggle"
      title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {dark ? (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
        </svg>
      ) : (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
        </svg>
      )}
    </button>
  );
};

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <BrowserRouter>
          <DarkModeToggle />
          <AppRouter />
          <CookieConsent />
          <Deko />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}

export default App;
